// ToolBarView.tsx
// Copyright (c) 2020 Neomantra Corp

import React, { useState, useEffect, forwardRef, createRef } from 'react'
import { Pane, Button, TextInput, Spinner, Icon, Text, RadioGroup } from 'evergreen-ui'
import { useStickyState } from './ToolBarSupport'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import 'react-datepicker/dist/react-datepicker-cssmodules.css'

import { useHotkeys } from 'react-hotkeys-hook'

type RenderLogoProps = {
    color: string
}

const RenderLogo = ({ color }: RenderLogoProps) => {
    return (
        <svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 42 42'>
            <path
                d='M33.6,8.7c-0.3-0.3-0.9-0.6-1.4-0.6l-18.6,0c-0.5,0-1.2,0-1.7,0H6c-0.5,0-0.6,0.3-0.2,0.6l3.6,3.6   c0.3,0.3,1,0.6,1.4,0.6H12c0.5,0,0.8,0.4,0.8,0.8v4.5c0,0.5-0.4,0.8-0.8,0.8H9.7c-0.5,0-0.6,0.3-0.2,0.6l7.6,7.6    c0.3,0.3,0.6,0.2,0.6-0.2v-2.4c0-0.5,0.4-0.8,0.8-0.8h6.8c0.5,0,0.8,0.4,0.8,0.8v5.3c0,0.5,0.3,1.1,0.6,1.4l3.6,3.6 c0.3,0.3,0.6,0.2,0.6-0.2v-10c0-0.5,0-0.9,0-0.9c0,0,0-0.4,0-0.9V20c0-0.5,0-0.9,0-0.9c0,0,0-0.4,0-0.9v-4.5c0-0.5,0.4-0.8,0.8-0.8  h5.1c0.5,0,0.6-0.3,0.3-0.6L33.6,8.7z M18.5,19c-0.5,0-0.8-0.4-0.8-0.8v-4.5c0-0.5,0.4-0.8,0.8-0.8h6.8c0.5,0,0.8,0.4,0.8,0.8v4.5   c0,0.5-0.4,0.8-0.8,0.8H18.5z'
                fill={color}
            />
        </svg>
    )
}

const CustomDatePickerInput = forwardRef((props: any, ref: any) => {
    return (
        <Button appearance='minimal' ref={ref} onClick={props.onClick}>
            {props.value}
        </Button>
    )
})

type ToolBarViewProps = {
    errorMsg: any
    sendRequest(evt: any, exchange: string, openClose: string, ticker: string): any
    setVotiveProp(votive: string): any
    votiveProp: any
    isLoading: boolean
    isConnected: boolean
}

const ToolBarView = ({ errorMsg, sendRequest, setVotiveProp, votiveProp, isLoading, isConnected }: ToolBarViewProps) => {
    const [ticker, setTicker] = useState(''),
        [votive, setVotive] = useStickyState('wss://', 'votiveConsoleState'),
        [exchange, setExchange] = useStickyState('itch', 'exchangeState'),
        [openClose, setOpenClose] = useStickyState('imbO', 'openCloseState'),
        [votiveEditing, setVotiveEditing] = useState(false),
        [selectedDate, setSelectedDate] = useState(Date.now()),
        [startOpenDate, setStartOpenDate] = useState(false),
        dateButtonRef = createRef(),
        textInputRef = createRef(),
        [textInputFocus, setTextInputFocus] = useState(false),
        panePadding = 12

    useHotkeys('shift+d', () => setStartOpenDate((prevOpenDate: any) => (prevOpenDate ? false : true)), {
        filter: function (event) {
            return true
        },
    })
    useHotkeys('shift+n', () => setExchange((prevExchange: any) => (prevExchange === 'itch' ? 'nyse' : 'itch')))
    useHotkeys('shift+o', () => setOpenClose(() => 'imbO'))
    useHotkeys('shift+c', () => setOpenClose(() => 'imbC'))
    useHotkeys('shift+s', () => setTextInputFocus((prevTextInputFocus: any) => (prevTextInputFocus ? false : true)))

    const setKeyUp = (event: any) => {
        if (event.key === 'Enter') {
            sendRequest(event, exchange, openClose, ticker)
            setTicker('')
        }
    }

    const setNewVotive = (event: any) => {
        setVotiveEditing(false)
        setVotiveProp(votive)
    }

    useEffect(() => {
        // TODO: how to get type for focus
        /*// @ts-ignore */
        textInputRef.current.focus()
    }, [textInputFocus])

    useEffect(() => {
        // TODO: how to get type for setOpen
        /*// @ts-ignore */
        dateButtonRef.current.setOpen(startOpenDate)
    }, [startOpenDate])

    useEffect(() => {
        setVotive(votiveProp)
        /*// @ts-ignore */
        textInputRef.current.focus()
        //initFocus()
    }, [])

    return (
        <Pane paddingBottom={0}>
            <Pane display='flex' alignItems='left' background='#e9e9e9' borderRadius={0}>
                <Pane display='flex' flexDirection='row' paddingRight={0}>
                    <Pane padding={0} paddingTop={15} paddingLeft={12}>
                        <RenderLogo color={isConnected ? 'green' : 'grey'} />
                    </Pane>
                </Pane>
                <Pane display='flex' padding={10} paddingTop={10} paddingLeft={2}>
                    <Button appearance='minimal' marginTop={7} flex={1} onClick={() => setVotiveEditing(!votiveEditing)} marginLeft={5}>
                        <Icon color='grey' icon={votiveEditing ? 'cog' : 'cog'} size={16} />
                        <Icon marginLeft={7} color='grey' icon={votiveEditing ? 'chevron-up' : 'chevron-down'} size={16} />
                    </Button>
                </Pane>
                <Pane paddingRight={7} paddingLeft={4} paddingTop={5}>
                    <RadioGroup
                        value={exchange}
                        options={[
                            { label: 'NASDAQ', value: 'itch' },
                            { label: 'NYSE', value: 'nyse' },
                        ]}
                        onChange={(value) => setExchange(value)}
                    />
                </Pane>
                <Pane padding={panePadding} paddingTop={15}>
                    <TextInput
                        innerRef={textInputRef}
                        height={36}
                        width={110}
                        onKeyUp={(event) => setKeyUp(event)}
                        onChange={(event) => setTicker(event.target.value.toUpperCase())}
                        value={ticker}
                        placeholder='Symbol...'
                    />
                </Pane>
                <Pane padding={7} paddingTop={5}>
                    <RadioGroup
                        value={openClose}
                        options={[
                            { label: 'Open', value: 'imbO' },
                            { label: 'Close', value: 'imbC' },
                        ]}
                        onChange={(value) => setOpenClose(value)}
                    />
                </Pane>
                <Pane padding={14} paddingTop={17} paddingRight={10}>
                    <Button disabled={ticker === ''} onClick={(e: any) => sendRequest(e, exchange, openClose, ticker)} marginRight={12}>
                        <Icon color='grey' icon='add-to-artifact' size={18} />
                    </Button>
                </Pane>
                <Pane paddingTop={17} paddingRight={10}>
                    <DatePicker
                        customInput={<CustomDatePickerInput />}
                        ref={dateButtonRef}
                        todayButton='Today'
                        selected={selectedDate}
                        onChange={(date) => setSelectedDate(date)}
                        maxDate={Date.now()}
                        dateFormat='MM / dd / yyyy'
                    />
                </Pane>
                <Pane paddingTop={12}>{isLoading ? <Spinner /> : null}</Pane>
                <Pane paddingTop={20}>
                    <Text>{errorMsg ? errorMsg : null}</Text>
                </Pane>
            </Pane>
            {votiveEditing && (
                <Pane padding={6} marginTop={0} marginBottom={0} display='flex' alignItems='left' background='#d5d5d5' borderRadius={0}>
                    <Icon margin={12} marginTop={25} color='#999' icon='cog' size={16} />
                    <Pane display='flex' flexDirection='column'>
                        <Text marginLeft={7} marginTop={3}>
                            Votive URL
                        </Text>
                        <Pane display='flex' flexDirection='row'>
                            <TextInput margin={5} height={36} width={240} onChange={(event) => setVotive(event.target.value)} value={votive} placeholder='Votive url...' />
                            <Button marginTop={7} height={33} onClick={(e) => setNewVotive(e)} marginRight={12}>
                                Connect
                            </Button>
                        </Pane>
                    </Pane>
                </Pane>
            )}
        </Pane>
    )
}

export default ToolBarView
